type CoverLetterPdfOptions = {
  authorName?: string
  company?: string
  position?: string
  recruiterName?: string
}

type CoverLetterPdfProperties = {
  title: string
  subject: string
  author?: string
  keywords: string
  creator: string
}

const DEFAULT_FILENAME_PREFIX = 'Cover_Letter'
const DEFAULT_JSPDF_PRODUCER = 'jsPDF 4.0.0'
const PAGE_MARGIN_PT = 72
const TOP_HEADER_Y_PT = 60
const FIRST_PAGE_BODY_Y_PT = 126
const FOLLOW_UP_PAGE_BODY_Y_PT = 84
const BOTTOM_MARGIN_PT = 72
const PARAGRAPH_GAP_PT = 14
const LINE_GAP_PT = 18

const getTimestamp = () => new Date().toISOString().split('T')[0]

const sanitizeFilenameSegment = (value?: string) => {
  if (!value) {
    return ''
  }

  return value
    .trim()
    .replace(/[^A-Za-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 48)
}

const normalizeLetterContent = (value: string) =>
  value
    .replace(/\r\n/g, '\n')
    .replace(/\u00a0/g, ' ')
    .trim()

const buildRoleLabel = (options: CoverLetterPdfOptions) => {
  const company = options.company?.trim()
  const position = options.position?.trim()

  if (position && company) {
    return `${position} at ${company}`
  }

  return position || company || 'Cover Letter'
}

const getDisplayAuthorName = (authorName?: string) => {
  const trimmed = authorName?.trim()
  return trimmed || 'Cover Letter'
}

const getProducerOverride = (authorName?: string) =>
  (authorName || 'User')
    .replace(/[^\x20-\x7e]/g, '')
    .trim()
    .slice(0, DEFAULT_JSPDF_PRODUCER.length)
    .padEnd(DEFAULT_JSPDF_PRODUCER.length, ' ')

export const binaryPdfStringToBytes = (content: string) => {
  const bytes = new Uint8Array(content.length)

  for (let index = 0; index < content.length; index += 1) {
    bytes[index] = content.charCodeAt(index) & 0xff
  }

  return bytes
}

const downloadPdfContent = (content: string, filename: string) => {
  const blob = new Blob([binaryPdfStringToBytes(content)], { type: 'application/pdf' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

const replaceProducerStamp = (content: string, authorName?: string) =>
  content.replace(
    `/Producer (${DEFAULT_JSPDF_PRODUCER})`,
    `/Producer (${getProducerOverride(authorName)})`,
  )

const splitIntoBlocks = (letterContent: string) =>
  normalizeLetterContent(letterContent)
    .split(/\n\s*\n/)
    .map((block) => block.split('\n').map((line) => line.trim()).filter(Boolean))
    .filter((block) => block.length > 0)
    .map((block) => {
      const preserveLines =
        block.length > 1 &&
        block.every((line) => line.length <= 80)

      if (preserveLines) {
        return {
          kind: 'lines' as const,
          lines: block,
        }
      }

      return {
        kind: 'paragraph' as const,
        lines: [block.join(' ')],
      }
    })

const drawHeader = (
  pdf: import('jspdf').jsPDF,
  options: CoverLetterPdfOptions,
  isFirstPage: boolean,
) => {
  const pageWidth = pdf.internal.pageSize.getWidth()
  const authorName = getDisplayAuthorName(options.authorName)
  const roleLabel = buildRoleLabel(options)
  const dateLabel = new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date())

  if (isFirstPage) {
    pdf.setFont('Helvetica', 'bold')
    pdf.setFontSize(20)
    pdf.setTextColor(15, 23, 42)
    pdf.text(authorName, PAGE_MARGIN_PT, TOP_HEADER_Y_PT)

    pdf.setFont('Helvetica', 'normal')
    pdf.setFontSize(10)
    pdf.setTextColor(71, 85, 105)
    pdf.text(roleLabel, PAGE_MARGIN_PT, TOP_HEADER_Y_PT + 18)
    pdf.text(dateLabel, pageWidth - PAGE_MARGIN_PT, TOP_HEADER_Y_PT, { align: 'right' })

    pdf.setDrawColor(203, 213, 225)
    pdf.setLineWidth(1)
    pdf.line(PAGE_MARGIN_PT, TOP_HEADER_Y_PT + 34, pageWidth - PAGE_MARGIN_PT, TOP_HEADER_Y_PT + 34)
    return FIRST_PAGE_BODY_Y_PT
  }

  pdf.setFont('Helvetica', 'normal')
  pdf.setFontSize(9)
  pdf.setTextColor(100, 116, 139)
  pdf.text(authorName, PAGE_MARGIN_PT, 42)
  pdf.text(roleLabel, pageWidth - PAGE_MARGIN_PT, 42, { align: 'right' })
  pdf.setDrawColor(226, 232, 240)
  pdf.setLineWidth(0.8)
  pdf.line(PAGE_MARGIN_PT, 54, pageWidth - PAGE_MARGIN_PT, 54)
  return FOLLOW_UP_PAGE_BODY_Y_PT
}

const addPage = (pdf: import('jspdf').jsPDF, options: CoverLetterPdfOptions) => {
  pdf.addPage()
  return drawHeader(pdf, options, false)
}

export const getCoverLetterPdfFilename = (options: CoverLetterPdfOptions = {}) => {
  const parts = [
    DEFAULT_FILENAME_PREFIX,
    sanitizeFilenameSegment(options.position),
    sanitizeFilenameSegment(options.company),
  ].filter(Boolean)

  return `${parts.join('_')}_${getTimestamp()}.pdf`
}

export const getCoverLetterPdfProperties = (
  options: CoverLetterPdfOptions = {},
): CoverLetterPdfProperties => {
  const authorName = options.authorName?.trim()
  const roleLabel = buildRoleLabel(options)

  return {
    title: `${roleLabel} Cover Letter`,
    subject: `Job application cover letter for ${roleLabel}`,
    author: authorName || undefined,
    keywords: 'cover letter, job application, pdf',
    creator: authorName || 'User',
  }
}

export const exportCoverLetterPdf = async (
  letterContent: string,
  options: CoverLetterPdfOptions = {},
): Promise<string> => {
  const normalizedLetter = normalizeLetterContent(letterContent)
  if (!normalizedLetter) {
    throw new Error('Cover letter content is required for PDF export')
  }

  const { jsPDF } = await import('jspdf')
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'pt',
    format: 'letter',
    compress: true,
  })

  const properties = getCoverLetterPdfProperties(options)
  pdf.setDocumentProperties(properties)
  pdf.setProperties(properties)

  const pageHeight = pdf.internal.pageSize.getHeight()
  const maxWidth = pdf.internal.pageSize.getWidth() - PAGE_MARGIN_PT * 2
  const blocks = splitIntoBlocks(normalizedLetter)
  let cursorY = drawHeader(pdf, options, true)

  pdf.setFont('Times', 'normal')
  pdf.setFontSize(12)
  pdf.setTextColor(17, 24, 39)

  for (const block of blocks) {
    if (block.kind === 'paragraph') {
      const wrappedLines = pdf.splitTextToSize(block.lines[0], maxWidth) as string[]
      const requiredHeight = wrappedLines.length * LINE_GAP_PT + PARAGRAPH_GAP_PT

      if (cursorY + requiredHeight > pageHeight - BOTTOM_MARGIN_PT) {
        cursorY = addPage(pdf, options)
        pdf.setFont('Times', 'normal')
        pdf.setFontSize(12)
        pdf.setTextColor(17, 24, 39)
      }

      pdf.text(wrappedLines, PAGE_MARGIN_PT, cursorY, { baseline: 'top' })
      cursorY += wrappedLines.length * LINE_GAP_PT + PARAGRAPH_GAP_PT
      continue
    }

    const requiredHeight = block.lines.length * LINE_GAP_PT + PARAGRAPH_GAP_PT
    if (cursorY + requiredHeight > pageHeight - BOTTOM_MARGIN_PT) {
      cursorY = addPage(pdf, options)
      pdf.setFont('Times', 'normal')
      pdf.setFontSize(12)
      pdf.setTextColor(17, 24, 39)
    }

    for (const line of block.lines) {
      const wrappedLines = pdf.splitTextToSize(line, maxWidth) as string[]
      const wrappedHeight = wrappedLines.length * LINE_GAP_PT

      if (cursorY + wrappedHeight > pageHeight - BOTTOM_MARGIN_PT) {
        cursorY = addPage(pdf, options)
        pdf.setFont('Times', 'normal')
        pdf.setFontSize(12)
        pdf.setTextColor(17, 24, 39)
      }

      pdf.text(wrappedLines, PAGE_MARGIN_PT, cursorY, { baseline: 'top' })
      cursorY += wrappedHeight
    }

    cursorY += PARAGRAPH_GAP_PT
  }

  const filename = getCoverLetterPdfFilename(options)
  const pdfContent = replaceProducerStamp(pdf.output(), options.authorName)
  downloadPdfContent(pdfContent, filename)
  return filename
}
